// The contract for the tracking identifiers carried from the storefront into a
// Shopify order.
//
// Shopify copies cart-level `attributes` onto the resulting order's
// `note_attributes`, which is what lets the `orders/paid` webhook attribute a
// purchase to the browser session that produced it. Without them the webhook
// knows only the customer's email — enough to key a PostHog person, useless for
// Meta or Google click attribution.
//
// This module is deliberately dependency-free so both halves can import it: the
// browser collector (`collect-tracking-attributes.ts`, which pulls in
// `posthog-js`) and the server action that writes them to Shopify. Keep it that
// way — the allowlist is the server's only defence and must not arrive through a
// client-only module.

export interface ShopifyCartAttributeInput {
  key: string;
  value: string;
}

/**
 * `_`-prefixed so Shopify hides them from the checkout UI and the
 * customer-facing order summary. They still reach the webhook.
 *
 * Two families, and the distinction matters when reading the values back:
 *   `_ph_*` / `_fbp` / `_fbc` / `_gclid` — identifiers, used to join to another
 *   system's record of this person.
 *   `_mk_*` — attribution context about how the visit began, used to describe
 *   the sale to the operator without a lookup (task 168).
 */
export const TRACKING_ATTRIBUTE_KEYS = {
  posthogDistinctId: "_ph_distinct_id",
  posthogSessionId: "_ph_session_id",
  fbp: "_fbp",
  fbc: "_fbc",
  gclid: "_gclid",
  entryReferrer: "_mk_entry_ref",
  utm: "_mk_utm",
  landingPath: "_mk_landing",
  /** Last-touch reorder-QR scan; see `print/reorder-qr-attribution.ts`. */
  reorderQr: "_mk_reorder",
} as const;

/**
 * Generous enough for every identifier we send (`_fbc` is the longest, and runs
 * well under 200 characters) and short enough that the attribute cannot be used
 * as a bulk data channel into the order record.
 */
export const MAX_TRACKING_ATTRIBUTE_VALUE_LENGTH = 256;

/**
 * Per-component bound for the packed `_mk_utm` value, chosen so all four
 * components together cannot breach `MAX_TRACKING_ATTRIBUTE_VALUE_LENGTH` and
 * get the whole attribute dropped: 4 × (`campaign=` + 50) still fits in 256.
 * Real sources, mediums, campaigns and keywords are far shorter than this.
 */
export const MAX_UTM_COMPONENT_LENGTH = 50;

export interface TrackingUtmParts {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
}

/**
 * Packs the UTM quartet into a single attribute, so the Shopify admin order view
 * shows one readable line rather than four keys.
 *
 * Encoded as a query string rather than `source|medium|campaign|term`: a search
 * keyword is free text and may well contain the delimiter, and `URLSearchParams`
 * round-trips it in both directions without a hand-written parser that could
 * break on it. Self-describing too, which matters because the operator reads
 * this raw in the Shopify admin.
 *
 * Returns `null` when there is nothing to say, so callers can skip the key.
 */
export function packUtmAttribute(parts: TrackingUtmParts): string | null {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(parts)) {
    const trimmed = value?.trim();
    if (trimmed) {
      params.set(key, trimmed.slice(0, MAX_UTM_COMPONENT_LENGTH));
    }
  }

  const packed = params.toString();
  return packed.length > 0 ? packed : null;
}

/**
 * Inverse of `packUtmAttribute`. Total in the face of junk — an unparseable or
 * absent value yields `{}` rather than throwing, because the only caller is
 * rendering an operator message that must never fail (see task 168's failure
 * isolation).
 */
export function parseUtmAttribute(value: string | null | undefined): TrackingUtmParts {
  if (!value) {
    return {};
  }

  try {
    const params = new URLSearchParams(value);
    const read = (key: string): string | undefined => params.get(key)?.trim() || undefined;

    return {
      source: read("source"),
      medium: read("medium"),
      campaign: read("campaign"),
      term: read("term"),
    };
  } catch {
    return {};
  }
}

const ALLOWED_KEYS = new Set<string>(Object.values(TRACKING_ATTRIBUTE_KEYS));

/**
 * Narrows a client-supplied list to the identifiers we actually recognise.
 *
 * This is a trust boundary, not a tidy-up: the values arrive from the browser
 * and are written into an order record the operator reads, so anything outside
 * the allowlist — or over the length bound — is dropped rather than forwarded.
 */
export function filterAllowedTrackingAttributes(
  attributes: readonly ShopifyCartAttributeInput[],
): ShopifyCartAttributeInput[] {
  return attributes.filter(
    (attribute) =>
      ALLOWED_KEYS.has(attribute.key) &&
      typeof attribute.value === "string" &&
      attribute.value.length > 0 &&
      attribute.value.length <= MAX_TRACKING_ATTRIBUTE_VALUE_LENGTH,
  );
}

/**
 * Reads the tracking attributes back off a raw `orders/*` webhook payload.
 *
 * Shopify surfaces cart-level attributes as the order's `note_attributes`, in
 * `{ name, value }` shape rather than the `{ key, value }` they were written
 * with — this is the one place that translation lives.
 *
 * Empty for any customer who declined consent. That is the designed outcome,
 * not a failure, and every caller must treat it as ordinary.
 *
 * Runs the result back through `filterAllowedTrackingAttributes` rather than
 * taking every `_`-prefixed entry: Shopify and its apps write note attributes of
 * their own, a buyer can in principle get text into this list, and the values
 * end up in an order record the operator reads. Same allowlist as the write
 * side, deliberately.
 *
 * CANNOT THROW, and that is load-bearing rather than tidy. This runs inside
 * `normalizeShopifyPaidOrderWebhook`, so an exception would fail normalization
 * of a *paid order*: the webhook route would answer 500, Shopify would retry,
 * and the print job would not be enqueued on that attempt. Attribution is worth
 * nothing next to that, so every input — including shapes Shopify has never sent
 * — yields `{}` rather than an error.
 */
export function extractTrackingAttributesFromOrderPayload(
  payload: unknown,
): Record<string, string> {
  try {
    if (!payload || typeof payload !== "object") {
      return {};
    }

    const raw = (payload as Record<string, unknown>).note_attributes;
    if (!Array.isArray(raw)) {
      return {};
    }

    const candidates = raw.flatMap((entry) => {
      if (!entry || typeof entry !== "object") {
        return [];
      }
      const { name, value } = entry as Record<string, unknown>;
      return typeof name === "string" && typeof value === "string" ? [{ key: name, value }] : [];
    });

    return Object.fromEntries(
      filterAllowedTrackingAttributes(candidates).map(({ key, value }) => [key, value]),
    );
  } catch {
    return {};
  }
}
